/**
 * Known web app domains and suffixes for illumi deep links.
 * When matched, the host portion is treated as the web domain rather than
 * a path segment (e.g. illumi://app.illumi.one/whiteboards/123 -> /whiteboards/123).
 *
 * Keep in sync with `desktop/deep-link.ts` (separate TS projects; cannot share).
 */
export const KNOWN_WEB_DOMAINS = [
	"localhost",
	"illumi.one",
	"illumi.systems",
	"illumi.sh",
] as const;

/**
 * Checks whether a given host or hostname matches any of the known web domains
 * (apex or any subdomain, ignoring port if present).
 */
export function isKnownWebDomain(host: string): boolean {
	if (!host) {
		return false;
	}
	const normalized = host.split(":")[0].toLowerCase();
	return KNOWN_WEB_DOMAINS.some(
		(domain) => normalized === domain || normalized.endsWith(`.${domain}`),
	);
}

/**
 * Splits a target path or URL into a sanitized relative path (leading
 * slashes/backslashes stripped, backslashes replaced with forward slashes)
 * and query/hash fragment.
 */
export function cleanPathAndQuery(targetPath: string): {
	cleanPath: string;
	queryHash: string;
} {
	const [pathPart, queryHash = ""] = (targetPath || "").split(/(?=[?#])/);
	const cleanPath = (pathPart || "").replace(/^[/\\]+/, "").replace(/\\/g, "/");
	return { cleanPath, queryHash };
}

/**
 * Normalizes a route path to ensure a single leading slash and preserved
 * query/hash.
 */
export function normalizeRoutePath(rawRoute: string): string {
	const { cleanPath, queryHash } = cleanPathAndQuery(rawRoute);
	return cleanPath ? `/${cleanPath}${queryHash}` : `/${queryHash}`;
}
